import {
  createMatomoPageviewId,
  getOptionalAnalyticsAuthToken,
  getOrCreateAnalyticsClientId,
  getOrCreateMatomoVisitorId,
} from './analyticsIdentity';
import { isAutomatedBrowser } from './automatedTraffic';

export type MatomoTrackKind = 'pageview' | 'event';

export interface MatomoTrackPayload {
  kind: MatomoTrackKind;
  url: string;
  title?: string;
  referrer?: string;
  event_category?: string;
  event_action?: string;
  event_name?: string;
  event_value?: number;
  client_id?: string;
  visitor_id?: string;
  pageview_id?: string;
  custom_dimensions?: Record<string, string>;
  metadata?: Record<string, unknown>;
}

export interface MatomoEventInput {
  category: string;
  action: string;
  name?: string;
  value?: number;
  url?: string;
  customDimensions?: Record<string, string>;
  metadata?: Record<string, unknown>;
}

const ENABLE_LOCAL_MATOMO =
  process.env.NEXT_PUBLIC_ENABLE_LOCAL_MATOMO === '1' ||
  process.env.NEXT_PUBLIC_ENABLE_LOCAL_OBSERVABILITY === '1';
const EARLY_EVENT_BOOTSTRAP_DELAY_MS = 650;
const SAME_ORIGIN_MATOMO_FALLBACK_URL = '/api/analytics/track';
const DEFAULT_PUBLIC_MATOMO_TRACKING_URL =
  'https://api.city-scope.co.uk/api/v1/analytics/track';

let currentPageviewId: string | null = null;
let lastPageviewUrl: string | null = null;
let lastPageviewPromise: Promise<boolean> | null = null;
let trackingQueue: Promise<unknown> = Promise.resolve();

function isLocalhost(): boolean {
  if (typeof window === 'undefined') return false;
  return ['localhost', '127.0.0.1'].includes(window.location.hostname);
}

export function shouldEnableMatomoTracking(): boolean {
  if (process.env.NEXT_PUBLIC_MATOMO_TRACKING_ENABLED === '0') return false;
  if (process.env.NEXT_PUBLIC_MATOMO_TRACKING_ENABLED === 'false') return false;
  if (isAutomatedBrowser()) return false;
  if (isLocalhost() && !ENABLE_LOCAL_MATOMO) return false;
  return true;
}

function normalizeAnalyticsPath(pathname: string): string {
  let path = (pathname || '').trim().replace(/\/+$/, '');
  if (!path) path = '/';

  const lower = path.toLowerCase();
  if (lower.endsWith('/api/v1/accounts')) {
    return `${path.slice(0, -'/api/v1/accounts'.length)}/api/v1/analytics/track`;
  }
  if (lower.endsWith('/api/v1/analytics')) {
    return `${path}/track`;
  }
  if (lower.endsWith('/api/v1/analytics/track')) {
    return path;
  }
  if (lower.endsWith('/accounts')) {
    return `${path.slice(0, -'/accounts'.length)}/analytics/track`;
  }
  if (path === '/') return '/api/v1/analytics/track';
  if (lower.endsWith('/api')) return `${path}/v1/analytics/track`;
  if (lower.endsWith('/api/v1')) return `${path}/analytics/track`;
  return `${path}/api/v1/analytics/track`;
}

export function getMatomoTrackingUrl(): string | null {
  if (typeof window !== 'undefined') {
    return process.env.NEXT_PUBLIC_MATOMO_TRACKING_API_URL?.trim() ||
      DEFAULT_PUBLIC_MATOMO_TRACKING_URL;
  }

  const explicitEndpoint = process.env.NEXT_PUBLIC_MATOMO_TRACKING_API_URL?.trim();
  if (explicitEndpoint) {
    return explicitEndpoint.replace(/\/+$/, '');
  }

  const rawBase = process.env.NEXT_PUBLIC_API_URL?.trim() || 'https://api.city-scope.co.uk';

  if (!rawBase) return null;

  if (rawBase.startsWith('/')) {
    return normalizeAnalyticsPath(rawBase);
  }

  try {
    const url = new URL(rawBase);
    return url.origin + normalizeAnalyticsPath(url.pathname);
  } catch {
    return null;
  }
}

export function getCurrentAnalyticsUrl(pathOverride?: string): string {
  if (typeof window === 'undefined') return pathOverride || '/';

  if (pathOverride) {
    try {
      const parsed = new URL(pathOverride, window.location.origin);
      return `${parsed.pathname}${parsed.search || ''}${parsed.hash || ''}`;
    } catch {
      return pathOverride;
    }
  }

  const { pathname = '/', search = '', hash = '' } = window.location;
  return `${pathname}${search || ''}${hash || ''}`;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }
    window.setTimeout(resolve, ms);
  });
}

function pageviewIdForUrl(url: string): string | undefined {
  return url === lastPageviewUrl ? currentPageviewId || undefined : undefined;
}

export function buildMatomoPageviewPayload(input: {
  url?: string;
  title?: string;
  referrer?: string;
  customDimensions?: Record<string, string>;
  metadata?: Record<string, unknown>;
} = {}): MatomoTrackPayload | null {
  const clientId = getOrCreateAnalyticsClientId();
  if (!clientId) return null;
  const visitorId = getOrCreateMatomoVisitorId();
  const pageviewId = createMatomoPageviewId();

  const title =
    input.title ||
    (typeof document !== 'undefined' ? document.title : undefined) ||
    'City Scope';

  return {
    kind: 'pageview',
    url: input.url || getCurrentAnalyticsUrl(),
    title,
    referrer: input.referrer,
    client_id: clientId,
    visitor_id: visitorId || undefined,
    pageview_id: pageviewId,
    custom_dimensions: input.customDimensions,
    metadata: input.metadata,
  };
}

async function postMatomoPayload(payload: MatomoTrackPayload): Promise<boolean> {
  if (!shouldEnableMatomoTracking()) return false;

  const endpoint = getMatomoTrackingUrl();
  if (!endpoint) return false;

  const headers = new Headers({
    'Content-Type': 'application/json',
    'X-CityScope-Interactive': 'analytics',
  });
  const token = await getOptionalAnalyticsAuthToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const post = (url: string) => fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
    });

  try {
    let response = await post(endpoint);
    if (
      typeof window !== 'undefined' &&
      endpoint !== SAME_ORIGIN_MATOMO_FALLBACK_URL &&
      (response.status === 429 || response.status >= 500)
    ) {
      response = await post(SAME_ORIGIN_MATOMO_FALLBACK_URL);
    }
    if (!response.ok) return false;

    try {
      const body = await response.json() as { accepted?: unknown };
      return body?.accepted !== false;
    } catch {
      return true;
    }
  } catch {
    if (typeof window === 'undefined' || endpoint === SAME_ORIGIN_MATOMO_FALLBACK_URL) {
      return false;
    }
    try {
      const fallbackResponse = await post(SAME_ORIGIN_MATOMO_FALLBACK_URL);
      if (!fallbackResponse.ok) return false;
      const body = await fallbackResponse.json() as { accepted?: unknown };
      return body?.accepted !== false;
    } catch {
      return false;
    }
  }
}

function enqueueMatomoPayload(payload: MatomoTrackPayload): Promise<boolean> {
  const task = trackingQueue.then(() => postMatomoPayload(payload));
  trackingQueue = task.catch(() => undefined);
  return task;
}

function sendEventAfterCurrentPageview(payload: MatomoTrackPayload): void {
  const send = async () => {
    if (payload.url !== lastPageviewUrl) {
      await wait(EARLY_EVENT_BOOTSTRAP_DELAY_MS);
    }

    if (payload.url === lastPageviewUrl && lastPageviewPromise) {
      await lastPageviewPromise.catch(() => false);
    }

    const payloadWithPageview = {
      ...payload,
      pageview_id: pageviewIdForUrl(payload.url) || payload.pageview_id,
    };
    void enqueueMatomoPayload(payloadWithPageview);
  };

  void send();
}

export function trackMatomoPageview(input: {
  url?: string;
  title?: string;
  referrer?: string;
  customDimensions?: Record<string, string>;
  metadata?: Record<string, unknown>;
} = {}): void {
  if (!shouldEnableMatomoTracking()) return;
  const payload = buildMatomoPageviewPayload(input);
  if (!payload) return;

  currentPageviewId = payload.pageview_id || null;
  lastPageviewUrl = payload.url;
  lastPageviewPromise = enqueueMatomoPayload(payload);
  void lastPageviewPromise;
}

export function trackMatomoEvent(input: MatomoEventInput): void {
  if (!shouldEnableMatomoTracking()) return;
  const clientId = getOrCreateAnalyticsClientId();
  if (!clientId) return;
  const visitorId = getOrCreateMatomoVisitorId();
  const url = input.url || getCurrentAnalyticsUrl();

  const payload: MatomoTrackPayload = {
    kind: 'event',
    url,
    title: input.name || input.action,
    event_category: input.category,
    event_action: input.action,
    event_name: input.name,
    event_value: input.value,
    client_id: clientId,
    visitor_id: visitorId || undefined,
    pageview_id: pageviewIdForUrl(url),
    custom_dimensions: input.customDimensions,
    metadata: input.metadata,
  };

  sendEventAfterCurrentPageview(payload);
}
